import type {
  RadarComponentSpec,
  RadarSeriesDefFE,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";

// ── State types ────────────────────────────────────────────────

export type RadarVertexDimension = "metric" | "player" | "drill" | "position";

export interface RadarMetricEntry {
  id: string;
  metricId: string | null;
  metricAgg: "avg" | "sum" | "best";
  metricIdentifierType: "metric" | "acwr";
  displayNameOverride?: string;
  colorOverride?: string;
  fillArea?: boolean;
  // Per-metric custom scale range, used when normalization === "custom" on a
  // metric-vertex radar (each axis is a different metric, so each needs its own range).
  customMin?: number;
  customMax?: number;
}

export interface RadarSubjectEntry {
  subjectFilter: string;
  displayName: string;
  queryIds: string[];
  displayNameOverride?: string;
  colorOverride?: string;
  fillArea?: boolean;
}

export interface RadarFormState extends DateRangeFormFields {
  title: string;
  showLegend: boolean;
  normalization: "player_minmax" | "team_zscore" | "custom";
  customMin: number;
  customMax: number;
  vertexDimension: RadarVertexDimension;
  metrics: RadarMetricEntry[];
  subjects: RadarSubjectEntry[];
  expandedKey: string | string[];
}

// ── Actions ────────────────────────────────────────────────────

export type RadarAction =
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_NORMALIZATION"; normalization: "player_minmax" | "team_zscore" | "custom" }
  | { type: "SET_CUSTOM_RANGE"; customMin?: number; customMax?: number }
  | { type: "SET_VERTEX_DIMENSION"; vertexDimension: RadarVertexDimension; subjects?: RadarSubjectEntry[]; metrics?: RadarMetricEntry[] }
  | { type: "POPULATE_INITIAL_METRICS"; metrics: RadarMetricEntry[] }
  | { type: "ADD_METRIC" }
  | { type: "REMOVE_METRIC"; index: number }
  | { type: "UPDATE_METRIC"; index: number; patch: Partial<Omit<RadarMetricEntry, "id">> }
  | { type: "ADD_SUBJECT" }
  | { type: "REMOVE_SUBJECT"; index: number }
  | { type: "UPDATE_SUBJECT"; index: number; patch: Partial<Omit<RadarSubjectEntry, "queryIds">> }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] }
  | { type: "UPDATE_DATE_RANGE"; patch: Partial<DateRangeFormFields> };

const MIN_ENTITY_VERTICES = 3;

// ── Reducer ────────────────────────────────────────────────────

export function radarReducer(state: RadarFormState, action: RadarAction): RadarFormState {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.title };

    case "SET_NORMALIZATION": {
      const norm =
        state.vertexDimension !== "metric" && action.normalization === "player_minmax"
          ? "team_zscore" as const
          : action.normalization;
      return { ...state, normalization: norm };
    }

    case "SET_CUSTOM_RANGE":
      return {
        ...state,
        customMin: action.customMin ?? state.customMin,
        customMax: action.customMax ?? state.customMax,
      };

    case "SET_VERTEX_DIMENSION": {
      if (action.vertexDimension === state.vertexDimension) return state;
      const willBeEntity = action.vertexDimension !== "metric";
      const newMetrics = action.metrics ?? state.metrics;
      const nMetrics = newMetrics.length;
      let newSubjects: RadarSubjectEntry[];
      if (action.subjects) {
        newSubjects = action.subjects.map((s) => ({
          ...s,
          queryIds: Array.from({ length: nMetrics }, (_, i) => s.queryIds[i] ?? crypto.randomUUID()),
        }));
      } else if (willBeEntity) {
        newSubjects = Array.from({ length: MIN_ENTITY_VERTICES }, () => ({
          subjectFilter: "",
          displayName: "",
          queryIds: Array.from({ length: nMetrics }, () => crypto.randomUUID()),
        }));
      } else {
        newSubjects = [createSubjectEntry(nMetrics)];
      }
      const normalization =
        action.vertexDimension !== "metric" && state.normalization === "player_minmax"
          ? "team_zscore" as const
          : state.normalization;
      return { ...state, vertexDimension: action.vertexDimension, metrics: newMetrics, subjects: newSubjects, normalization };
    }

    case "POPULATE_INITIAL_METRICS": {
      const nNew = action.metrics.length;
      const resizedSubjects = state.subjects.map((s) => ({
        ...s,
        queryIds: Array.from({ length: nNew }, (_, i) => s.queryIds[i] ?? crypto.randomUUID()),
      }));
      return { ...state, metrics: action.metrics, subjects: resizedSubjects };
    }

    case "ADD_METRIC":
      return {
        ...state,
        metrics: [...state.metrics, createMetricEntry()],
        subjects: state.subjects.map((s) => ({
          ...s,
          queryIds: [...s.queryIds, crypto.randomUUID()],
        })),
      };

    case "REMOVE_METRIC": {
      const minMetrics = state.vertexDimension !== "metric" ? 1 : 3;
      if (state.metrics.length <= minMetrics) return state;
      return {
        ...state,
        metrics: state.metrics.filter((_, i) => i !== action.index),
        subjects: state.subjects.map((s) => ({
          ...s,
          queryIds: s.queryIds.filter((_, i) => i !== action.index),
        })),
      };
    }

    case "UPDATE_METRIC": {
      const patch = action.patch;
      const resetAcwr =
        "metricId" in patch && patch.metricId != null && !patch.metricId.startsWith("gps-")
          ? { metricIdentifierType: "metric" as const }
          : {};
      return {
        ...state,
        metrics: state.metrics.map((m, i) =>
          i === action.index ? { ...m, ...patch, ...resetAcwr } : m,
        ),
      };
    }

    case "ADD_SUBJECT": {
      const nMetrics = state.metrics.length;
      const isEntityVertex = state.vertexDimension !== "metric";
      const newSubject = isEntityVertex
        ? { subjectFilter: "", displayName: "", queryIds: Array.from({ length: nMetrics }, () => crypto.randomUUID()) }
        : createSubjectEntry(nMetrics);
      return { ...state, subjects: [...state.subjects, newSubject] };
    }

    case "REMOVE_SUBJECT": {
      if (state.subjects.length <= 1) return state;
      return {
        ...state,
        subjects: state.subjects.filter((_, i) => i !== action.index),
      };
    }

    case "UPDATE_SUBJECT":
      return {
        ...state,
        subjects: state.subjects.map((s, i) =>
          i === action.index ? { ...s, ...action.patch } : s,
        ),
      };

    case "SET_EXPANDED_KEY":
      return { ...state, expandedKey: action.key };

    case "UPDATE_DATE_RANGE":
      return { ...state, ...action.patch };

    default:
      return state;
  }
}

// ── Factories ──────────────────────────────────────────────────

function createMetricEntry(): RadarMetricEntry {
  return { id: crypto.randomUUID(), metricId: null, metricAgg: "avg", metricIdentifierType: "metric" };
}

function createSubjectEntry(queryIdCount: number): RadarSubjectEntry {
  return {
    subjectFilter: "full-team",
    displayName: "Full Team",
    queryIds: Array.from({ length: queryIdCount }, () => crypto.randomUUID()),
  };
}

export function createInitialState(): RadarFormState {
  return {
    title: "",
    showLegend: true,
    normalization: "player_minmax",
    customMin: 0,
    customMax: 100,
    vertexDimension: "metric",
    metrics: [createMetricEntry(), createMetricEntry(), createMetricEntry()],
    subjects: [createSubjectEntry(3)],
    expandedKey: "__dimensions",
    ...DATE_RANGE_DEFAULTS,
  };
}

// ── Aggregate prefix ───────────────────────────────────────────

export const AGG_PREFIX: Record<"avg" | "sum" | "best", string> = {
  avg: "[A]",
  sum: "[S]",
  best: "[B]",
};

// ── Spec serialization ─────────────────────────────────────────

export function formStateToSpec(
  state: RadarFormState,
  metrics: MetricOption[],
  _getSubjectEntities?: (t: string) => SubjectEntity[],
): RadarComponentSpec | null {
  const configuredIndices = state.metrics
    .map((m, i) => (m.metricId != null ? i : -1))
    .filter((i) => i >= 0);

  const isEntityVertex = state.vertexDimension !== "metric";

  const validSubjects = isEntityVertex
    ? state.subjects.filter((s) => s.subjectFilter !== "")
    : state.subjects;

  if (isEntityVertex) {
    if (configuredIndices.length < 1) return null;
    if (validSubjects.length < 3) return null;
  } else {
    if (configuredIndices.length < 3) return null;
    if (validSubjects.length === 0) return null;
  }

  const configuredMetrics = configuredIndices.map((i) => state.metrics[i]);

  // metric_names stores processed metric display labels (with agg prefix or override).
  // Length must match series[i].queryIds.length — the backend validates this.
  const metricNames = configuredMetrics.map((m) => {
    const opt = metrics.find((o) => o.id === m.metricId);
    const baseName = opt?.name ?? m.metricId ?? "";
    return m.displayNameOverride ?? `${AGG_PREFIX[m.metricAgg]} ${baseName}`;
  });

  const series: RadarSeriesDefFE[] = validSubjects.map((s) => ({
    displayName: s.displayName,
    subjectFilter: s.subjectFilter,
    queryIds: configuredIndices.map((i) => s.queryIds[i] ?? crypto.randomUUID()),
    colorOverride: s.colorOverride,
    fillArea: s.fillArea,
    displayNameOverride: s.displayNameOverride,
  }));

  return {
    type: "radar",
    display: { title: state.title, showLegend: state.showLegend },
    normalization: state.normalization,
    ...(state.normalization === "custom" && { customMin: state.customMin, customMax: state.customMax }),
    metric_names: metricNames,
    series,
    vertexDimension: state.vertexDimension,
    radar_builder_misc: {
      dateRange: state.dateRange,
      showAdvancedDate: state.showAdvancedDate,
      startDateType: state.startDateType,
      startDateLatestN: state.startDateLatestN,
      startDateAbsolute: state.startDateAbsolute,
      startDateOffset: state.startDateOffset,
      endDateType: state.endDateType,
      endDateAbsolute: state.endDateAbsolute,
      endDateOffset: state.endDateOffset,
      dayFilter: state.dayFilter,
      dateControllerId: state.dateControllerId,
      vertexDimension: state.vertexDimension,
      metrics: configuredMetrics.map((m) => {
        const opt = metrics.find((o) => o.id === m.metricId);
        return {
          metricId: m.metricId!,
          metricAgg: m.metricAgg,
          metricIdentifierType: m.metricIdentifierType,
          displayName: opt?.name ?? "",
          displayNameOverride: m.displayNameOverride,
          colorOverride: m.colorOverride,
          fillArea: m.fillArea,
          customMin: m.customMin,
          customMax: m.customMax,
        };
      }),
    },
  };
}

// ── Spec hydration ─────────────────────────────────────────────

export function hydrateFromSpec(spec: RadarComponentSpec): RadarFormState {
  const misc = spec.radar_builder_misc ?? {};
  const savedMetrics = (misc.metrics ?? []) as Array<{
    metricId: string;
    metricAgg: string;
    metricIdentifierType?: string;
    displayName?: string;
    displayNameOverride?: string;
    colorOverride?: string;
    fillArea?: boolean;
    customMin?: number;
    customMax?: number;
  }>;

  const vertexDimension = (spec.vertexDimension ?? misc.vertexDimension ?? "metric") as RadarVertexDimension;

  const metrics: RadarMetricEntry[] = savedMetrics.map((m) => ({
    id: crypto.randomUUID(),
    metricId: m.metricId,
    metricAgg: (m.metricAgg ?? "avg") as "avg" | "sum" | "best",
    metricIdentifierType: (m.metricIdentifierType ?? "metric") as "metric" | "acwr",
    displayNameOverride: m.displayNameOverride,
    colorOverride: m.colorOverride,
    fillArea: m.fillArea,
    customMin: m.customMin,
    customMax: m.customMax,
  }));

  const paddedMetrics =
    metrics.length >= 3
      ? metrics
      : [...metrics, ...Array.from({ length: 3 - metrics.length }, createMetricEntry)];

  const nMetrics = paddedMetrics.length;

  const subjects: RadarSubjectEntry[] = spec.series.map((s) => ({
    subjectFilter: s.subjectFilter,
    displayName: s.displayName,
    queryIds:
      s.queryIds.length >= nMetrics
        ? s.queryIds.slice(0, nMetrics)
        : [...s.queryIds, ...Array.from({ length: nMetrics - s.queryIds.length }, () => crypto.randomUUID())],
    displayNameOverride: s.displayNameOverride,
    colorOverride: s.colorOverride,
    fillArea: s.fillArea,
  }));

  return {
    title: (spec.display?.title as string) ?? "",
    showLegend: true,
    normalization: (() => {
      const raw = (spec.normalization ?? (vertexDimension !== "metric" ? "team_zscore" : "player_minmax")) as "player_minmax" | "team_zscore" | "custom";
      return vertexDimension !== "metric" && raw === "player_minmax" ? "team_zscore" : raw;
    })(),
    customMin: (spec as any).customMin ?? 0,
    customMax: (spec as any).customMax ?? 100,
    vertexDimension,
    metrics: paddedMetrics,
    subjects: subjects.length > 0 ? subjects : [createSubjectEntry(nMetrics)],
    expandedKey: "__dimensions",
    dateRange: (misc.dateRange as string) ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: (misc.showAdvancedDate as boolean) ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateLatestN: misc.startDateLatestN,
    startDateAbsolute: (misc.startDateAbsolute as string) ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: (misc.startDateOffset as number) ?? DATE_RANGE_DEFAULTS.startDateOffset,
    endDateType: (misc.endDateType as "relative" | "absolute") ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: (misc.endDateAbsolute as string) ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: (misc.endDateOffset as number) ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: (misc.dayFilter as string[]) ?? DATE_RANGE_DEFAULTS.dayFilter,
    dateControllerId: misc.dateControllerId as string | undefined,
  };
}
